// The onboarding scorecard as the repo view renders it — a pure regrouping of
// `repositories.ai_first_score` (`AiFirstScore`, `entities/ai-first-score.ts`),
// served on `GET /v1/repos/:owner/:repo` as `ai_first_score`.
//
// NOTHING HERE GRADES. The box computed every item's state and evidence and the
// mothership stored it, latest run winning; this module only decides the ORDER a
// person reads it in. A state or an evidence line invented here would be a second
// scorecard beside the stored one.

import {
  AI_FIRST_ITEM_IDS,
  type AiFirstItemId,
  type AiFirstItemState,
  type AiFirstScore,
} from '@developerz/domain';

/**
 * The groups, in the order a person should read them: what is missing first,
 * what was tried and failed next, what a human declined, and what is proved last.
 * Every {@link AiFirstItemState} exactly once — `ai-first-score.test.ts` asserts
 * it against the domain enum, so a fifth state cannot be dropped for want of a slot.
 */
export const SCORE_STATE_ORDER: readonly AiFirstItemState[] = [
  'gap',
  'unprovable',
  'refused',
  'ok',
];

/** One item as a line: its id and the FIRST evidence entry, `null` when it carries none. */
export type ScoreLine = { readonly id: AiFirstItemId; readonly evidence: string | null };
export type ScoreGroup = { readonly state: AiFirstItemState; readonly items: readonly ScoreLine[] };

/**
 * The stored items grouped by state, in {@link SCORE_STATE_ORDER}, each group in
 * the domain's item order, empty groups omitted. Total: `items` is exhaustive
 * over the item ids (the schema refuses one missing), so every item lands in
 * exactly one group.
 */
export function scoreGroups(score: AiFirstScore): ScoreGroup[] {
  return SCORE_STATE_ORDER.map((state) => ({
    state,
    items: AI_FIRST_ITEM_IDS.filter((id) => score.items[id].state === state).map((id) => ({
      id,
      evidence: score.items[id].evidence[0] ?? null,
    })),
  })).filter((group) => group.items.length > 0);
}
